import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { getApiBase, getAuthHeaders } from '../../utils/api';
import { flightSide, flightDateTime, hasFlightScheduleConflict, flightProgress, formatAgo, formatDelayDuration } from '../../utils/flightDisplay';

const INSIGHTS_REFRESH_MS = 10 * 60000;
const INSIGHTS_LOOKAHEAD_MS = 30 * 24 * 3600000;

export function FlightFreshness({ leg = {}, prefs = {} }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => { const timer = setInterval(() => setNow(Date.now()), 60000); return () => clearInterval(timer); }, []);
  if (hasFlightScheduleConflict(leg)) return <div className="text-xs text-ui-amber-fg">Showing booked times · provider schedule needs verification</div>;

  const progress = flightProgress(leg, now);
  if (progress.completed) {
    const arrival = flightSide(leg, 'arrival', prefs);
    const label = progress.completedKind === 'cancelled' ? 'Final · cancelled'
      : progress.completedKind === 'arrived' ? `Final · arrived ${[arrival.date, arrival.time].filter(Boolean).join(', ')}`
      : 'Flight completed · arrival not confirmed by provider';
    return <div className="text-xs text-ui-muted">{label}</div>;
  }

  // Source publication and transport receipt are kept apart in the tooltip;
  // the label shows the newest evidence that the data changed.
  const observations = Object.values(leg.field_observations || {});
  const latest = key => observations.map(o => Date.parse(o[key] || '')).filter(Number.isFinite).sort((a, b) => b - a)[0];
  const observed = latest('observed_at');
  const received = latest('received_at');
  const changed = Date.parse(leg.last_fa_update || '');
  const newest = [observed, received, changed].filter(Number.isFinite).sort((a, b) => b - a)[0];
  const details = [
    Number.isFinite(observed) ? `Provider observed: ${new Date(observed).toISOString()}` : 'Provider observation time unavailable',
    Number.isFinite(received) ? `Received: ${new Date(received).toISOString()}` : null,
  ].filter(Boolean).join(' · ');
  const label = Number.isFinite(newest) ? `Updated ${formatAgo(newest, now)}`
    : leg.has_active_alert ? 'Awaiting first update' : 'Live updates unavailable';
  return <div className="text-xs text-ui-muted" title={Number.isFinite(newest) ? details : undefined}>{label}</div>;
}

function historyText(history) {
  const side = history?.arrival || history?.departure;
  const median = side?.median_delay_minutes;
  if (median == null) return null;
  const verb = history?.arrival ? 'arrives' : 'departs';
  const basis = side.flights_considered ? ` (median of ${side.flights_considered} recent flights)` : '';
  if (median <= 5) return `Usually ${verb} on time${basis}`;
  return `Usually ${verb} ${formatDelayDuration(median)} late${basis}`;
}

function inboundText(inbound) {
  if (!inbound) return null;
  const who = [inbound.ident, inbound.origin && `from ${inbound.origin}`].filter(Boolean).join(' ');
  const aircraft = `Inbound aircraft${who ? ` (${who})` : ''}`;
  const turn = inbound.turn_minutes != null ? ` · ${inbound.turn_minutes} min turnaround` : '';
  if (inbound.arrived) return `${aircraft} has arrived${turn}`;
  if (inbound.delay_minutes >= 15) return `${aircraft} running ${formatDelayDuration(inbound.delay_minutes)} late${turn}`;
  return `${aircraft} on time${turn}`;
}

export function FlightInsights({ leg = {}, flightId, prefs = {} }) {
  const [insights, setInsights] = useState(null);
  const legNumber = leg.leg_number || 1;
  const progress = flightProgress(leg);
  const departure = Date.parse(leg.estimated_departure || leg.scheduled_departure || leg.departure_datetime || '');
  const eligible = Boolean(flightId) && !progress.completed
    && (!Number.isFinite(departure) || departure - Date.now() < INSIGHTS_LOOKAHEAD_MS);

  useEffect(() => {
    if (!eligible) return undefined;
    let cancelled = false;
    const load = () => axios.get(`${getApiBase()}/flights/${encodeURIComponent(flightId)}/legs/${legNumber}/insights`, { headers: getAuthHeaders() })
      .then(response => { if (!cancelled) setInsights(response.data || null); })
      .catch(() => {});
    load();
    const timer = setInterval(load, INSIGHTS_REFRESH_MS);
    return () => { cancelled = true; clearInterval(timer); };
  }, [eligible, flightId, legNumber]);

  if (progress.completed) return null;
  const lines = [];
  const predicted = leg.predicted_arrival || insights?.predicted_arrival;
  if (predicted && !leg.actual_arrival) {
    const clock = flightDateTime(predicted, leg.arrival_timezone, prefs);
    if (clock.time) lines.push({ key: 'predicted', tone: 'muted', text: `Predicted arrival ${clock.time} (based on history)` });
  }
  if (insights?.inbound) {
    const tone = insights.inbound.level === 'late' ? 'bad' : insights.inbound.level === 'watch' ? 'warn' : 'muted';
    lines.push({ key: 'inbound', tone, text: inboundText(insights.inbound) });
  }
  const history = historyText(insights?.delay_history);
  if (history) lines.push({ key: 'history', tone: 'muted', text: history });
  Object.entries(insights?.airport_delays || {}).forEach(([side, info]) => {
    const median = info?.median_delay_minutes;
    if (median == null || (median < 15 && !(info.delay_index >= 2.5))) return;
    const label = side === 'departure' ? 'departures' : 'arrivals';
    lines.push({ key: `airport-${side}`, tone: median >= 30 ? 'warn' : 'muted',
      text: `${info.airport} ${label} currently running ${formatDelayDuration(Math.max(median, 0))} late (median)` });
  });
  if (!lines.length) return null;
  const toneClass = { muted: 'text-ui-muted', warn: 'text-ui-amber-fg', bad: 'text-ui-red-fg' };
  return <div className="space-y-0.5" data-testid="flight-insights">
    {lines.filter(line => line.text).map(line => <div key={line.key} className={`text-xs ${toneClass[line.tone]}`}>{line.text}</div>)}
  </div>;
}

export default function FlightDataSummary({ leg, prefs, flightId }) {
  if (!leg) return null;
  const dep = flightSide(leg, 'departure', prefs), arr = flightSide(leg, 'arrival', prefs);
  return <div className="mt-2 space-y-1 text-xs text-ui-muted">
    <div>Departure: {dep.kind.toLowerCase()}{dep.basis ? ` (${dep.basis})` : ''} · {dep.timezoneLabel}; arrival: {arr.kind.toLowerCase()}{arr.basis ? ` (${arr.basis})` : ''} · {arr.timezoneLabel}</div>
    {leg.operating_airline && String(leg.operating_airline).toUpperCase() !== String(leg.airline_code || '').toUpperCase() && <div>Operated by {leg.operating_airline_name || leg.operating_airline} {leg.operating_flight_number || ''}</div>}
    <FlightFreshness leg={leg} prefs={prefs} />
    <FlightInsights leg={leg} flightId={flightId} prefs={prefs} />
  </div>;
}
